import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getQueryParams } from 'utils/helpers/extras';
import { notifyErrorObject } from 'wrappers/reporting';

declare const Careem: {
  paymentHandlerModule: {
    initPayment: (props: Omit<PaymentHandlerProps, 'redirectURI'>) => Promise<{ paymentState: PaymentStatusEnum }>;
  };
  apiLevel: number;
};

interface PaymentHandlerProps {
  invoiceId: string;
  currency: string;
  amount: number;
  ctaTitle?: string;
  redirectURI: string;
}

enum PaymentStatusEnum {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

interface HandeCareemPaymentProps {
  handlePlaceOrder: (recurringInvoiceId: string) => void;
}

function useHandleCareemPayment({ handlePlaceOrder }: HandeCareemPaymentProps) {
  const { t } = useTranslation();

  const careemNewPaymentEnabled = useFeatureIsOn('cpay-jsbridge-enabled');

  const isNewPaymentModuleAvailable = useMemo(() => {
    try {
      if (careemNewPaymentEnabled && Careem?.apiLevel >= 9 && Careem?.paymentHandlerModule?.initPayment !== undefined) {
        return true;
      }
      return false;
    } catch (error) {
      notifyErrorObject(`Error checking Careem payment module availability: ${JSON.stringify(error)}`);
    }

    return false;
  }, [careemNewPaymentEnabled]);

  const handlePayment = async ({ ctaTitle = 'Place Your Order', redirectURI }: Omit<PaymentHandlerProps, 'invoiceId' | 'currency' | 'amount'>) => {
    // paymentHandlerModule.initPayment is available in API level 9 and above
    // otherwise, redirect to the provided redirectURI
    if (isNewPaymentModuleAvailable) {
      try {
        // extract invoiceId, amount, currency from redirectURI query params
        // and initiate payment if JSI new Payment module available.
        const { invoiceId, amount, currency } = getQueryParams(redirectURI);
        const { paymentState } = await Careem.paymentHandlerModule.initPayment({
          invoiceId,
          currency,
          amount: Number(amount),
          ctaTitle,
        });

        if (paymentState === PaymentStatusEnum.SUCCESS) {
          handlePlaceOrder(invoiceId);
        } else {
          notifyErrorObject(`Payment failed with status: ${paymentState} with inputs ${JSON.stringify({ invoiceId, currency, amount, ctaTitle })}`);
        }
      } catch (error: any) {
        toast.dark(error.errorDescription || t('common.initiatePaymentFailed'));
        notifyErrorObject(error);
        window.location.href = redirectURI;
      }
    } else {
      window.location.href = redirectURI;
    }
  };

  return {
    handlePayment,
    isNewPaymentModuleAvailable,
  };
}

export default useHandleCareemPayment;
